import React, { ReactNode, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

interface Props extends React.HTMLAttributes<HTMLDialogElement> {
    onClose: () => void;
    children: ReactNode;
}

/**
 * A centered modal dialog built on the native <dialog> element: opens in the
 * top layer with a backdrop, closes on Escape (the browser's 'cancel' event)
 * and on a backdrop click. `className` styles the panel itself.
 */
const CenteredDialog = ({ onClose, className, children, ...rest }: Props) => {
    const dialogRef = useRef<HTMLDialogElement>(null);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (dialog && !dialog.open) {
            dialog.showModal();
        }

        const handleCancel = (event: Event): void => {
            event.preventDefault();
            onClose();
        };

        dialog?.addEventListener('cancel', handleCancel);
        return () => {
            dialog?.removeEventListener('cancel', handleCancel);
        };
    }, [onClose]);

    const handleClick = (event: React.MouseEvent<HTMLDialogElement>): void => {
        // Close when the backdrop (the dialog element itself) is clicked.
        if (event.target === dialogRef.current) {
            onClose();
        }
    };

    return (
        <dialog
            {...rest}
            ref={dialogRef}
            onClick={handleClick}
            className={twMerge(
                'fixed inset-x-0 top-1/2 m-0 mx-auto box-border w-full max-w-99 -translate-y-1/2 backdrop:bg-black/70',
                className,
            )}
        >
            {children}
        </dialog>
    );
};

export default CenteredDialog;
